import React, { Fragment, useMemo, useState } from 'react';

import trim from 'lodash/trim';

import {
  Grid,
  Divider,
  Typography,
  SearchInput,
  Button,
  IconButton,
  Stack,
  Toolbar,
  makeStyles,
  VirtualizedList,
  CloseIcon,
} from '@gogame/components';
import { useMountedState } from '@gogame/hooks-ts';

import { getOptionLabel, searchPresets } from '../PresetList/PresetList';
import FolderIcon from '../components/FolderIcon';
import GroupActions from '../components/GroupActions';
import {
  ACTION_DELETE_GROUP,
  ACTION_RENAME_GROUP,
} from '../components/GroupActions/constants';

const useStyles = makeStyles({
  toolbar: {
    padding: '0 12px',
  },
  selectedToolbar: {
    backgroundColor: '#f6f6f6',
    justifyContent: 'space-between',
    padding: '0 12px',
  },
});

export const defaultExpandedGroupIds = [0];
const groupActions = [ACTION_RENAME_GROUP, ACTION_DELETE_GROUP];

const ManageGroupsList = ({ presets, onAddGroup, onMoveTo }) => {
  const classes = useStyles();
  const [searchQuery, setSearchQuery] = useMountedState('');
  const [selectedValues, setSelectedValues] = useState([]);
  const filteredPresets = useMemo(() => {
    return searchPresets({ presets, searchQuery });
  }, [JSON.stringify(presets), searchQuery]);

  const handleUnselectAll = () => {
    setSelectedValues([]);
  };

  const handleSelect = (_, opt) => {
    setSelectedValues(
      selectedValues.includes(opt.id)
        ? selectedValues.filter((id) => id !== opt.id)
        : [...selectedValues, opt.id]
    );
  };

  return (
    <React.Fragment>
      {selectedValues?.length ? (
        <Fragment>
          <Toolbar className={classes.selectedToolbar}>
            <Stack spacing={2} direction={'row'} alignItems={'center'}>
              <IconButton
                icon={<CloseIcon />}
                onClick={handleUnselectAll}
                tooltip={'Unselect all'}
              />
              <Typography
                variant={'subtitle1'}
              >{`${selectedValues.length} selected`}</Typography>
            </Stack>
            <Button
              onClick={() => {
                onMoveTo(selectedValues, handleUnselectAll);
              }}
            >
              Move to...
            </Button>
          </Toolbar>
          <Divider />
        </Fragment>
      ) : (
        <Toolbar className={classes.toolbar}>
          <Stack
            direction={'row'}
            spacing={2}
            justifyContent={'space-between'}
            alignItems={'center'}
            sx={{ flexGrow: 1 }}
          >
            <SearchInput
              style={{ flex: 1 }}
              placeholder={'Search'}
              value={searchQuery}
              variant={'filled'}
              onChange={(_e, value) => setSearchQuery(trim(value))}
            />
            <Grid item>
              <Button
                add
                onClick={() => onAddGroup(selectedValues, handleUnselectAll)}
                variant={'outlined'}
              >
                Group
              </Button>
            </Grid>
          </Stack>
        </Toolbar>
      )}
      {filteredPresets.length ? <Divider /> : null}
      <Stack flex={'1 0 auto'}>
        <VirtualizedList
          checkbox={(_, { isGroupItem }) => !isGroupItem}
          expandedIds={defaultExpandedGroupIds}
          data={filteredPresets}
          isExpanded={() => searchQuery}
          secondaryAction={(props, { isGroupItem }) =>
            isGroupItem ? (
              <GroupActions {...props} actions={groupActions} />
            ) : null
          }
          icon={FolderIcon}
          onItemClick={handleSelect}
          isChecked={(opt) => ({ checked: selectedValues.includes(opt.id) })}
          getOptionLabel={getOptionLabel}
          button
          gutterLabel
        />
      </Stack>
    </React.Fragment>
  );
};

export default ManageGroupsList;
